import { useEffect, useState } from 'react';
import { clientPortalApi, type MinimalPointFeature, type RouteMetricsResult } from '../api';
import { buildPavementGradientStops } from './pavementZones';
import type { SpeedGradientStops } from './TerritorialMap';

/** Dedup por `id` real — el mismo semáforo puede aparecer en el route-metrics de IDA y de VUELTA cuando el tramo es compartido; sin esto se dibujaba dos veces el mismo punto físico. */
function mergeFeatures(a: MinimalPointFeature[], b: MinimalPointFeature[]): MinimalPointFeature[] {
  const byId = new Map<string, MinimalPointFeature>();
  for (const f of [...a, ...b]) byId.set(f.id, f);
  return [...byId.values()];
}

const EMPTY_POINT_GROUP = { count: 0, features: [] as MinimalPointFeature[] };

/**
 * Infraestructura vial necesita AMBAS direcciones a la vez (mismo patrón
 * "overview" que Recorrido/Velocidades) — a diferencia de Cobertura, que
 * solo pide la dirección seleccionada. `route-metrics` es por dirección,
 * así que acá se piden las dos (o solo CIRCUITO si aplica) y se combinan:
 * pavimento queda por dirección (gradiente propio de cada línea, igual que
 * `speedStops`), semáforos/reductores/cruces se funden por `id` porque son
 * puntos, no tramos direccionales.
 */
export function useInfrastructureData(
  slug: string,
  token: string,
  analysisId: string,
  isCircuit: boolean
): {
  loadState: 'loading' | 'error' | 'ready';
  pavementStops: SpeedGradientStops;
  infraRouteMetrics: RouteMetricsResult | null;
} {
  const [loadState, setLoadState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [pavementStops, setPavementStops] = useState<SpeedGradientStops>({ IDA: [], VUELTA: [] });
  const [infraRouteMetrics, setInfraRouteMetrics] = useState<RouteMetricsResult | null>(null);

  useEffect(() => {
    if (!token || !analysisId) return;
    let cancelled = false;
    setLoadState('loading');

    const idaPromise = clientPortalApi.getRouteMetrics(slug, analysisId, token, isCircuit ? 'CIRCUITO' : 'IDA');
    const vueltaPromise = isCircuit ? null : clientPortalApi.getRouteMetrics(slug, analysisId, token, 'VUELTA');

    Promise.all([idaPromise, vueltaPromise ?? Promise.resolve(null)])
      .then(([ida, vuelta]) => {
        if (cancelled) return;
        setPavementStops({
          IDA: buildPavementGradientStops(ida.pavement.segments, ida.pavement.totalLengthM),
          VUELTA: vuelta ? buildPavementGradientStops(vuelta.pavement.segments, vuelta.pavement.totalLengthM) : [],
        });
        setInfraRouteMetrics({
          ...ida,
          trafficSignals: {
            features: mergeFeatures(ida.trafficSignals.features, vuelta?.trafficSignals.features ?? []),
            count: 0,
          },
          trafficCalming: {
            features: mergeFeatures(ida.trafficCalming.features, vuelta?.trafficCalming.features ?? []),
            count: 0,
          },
          crossings: { features: mergeFeatures(ida.crossings.features, vuelta?.crossings.features ?? []), count: 0 },
          railwayCrossings: EMPTY_POINT_GROUP,
          streetlights: EMPTY_POINT_GROUP,
        });
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return (): void => {
      cancelled = true;
    };
  }, [slug, token, analysisId, isCircuit]);

  // Los counts de trafficSignals/trafficCalming/crossings se derivan del
  // largo real ya mergeado — separado del efecto de arriba para no
  // recalcularlo dos veces si algún día el merge se vuelve más caro.
  const withCounts: RouteMetricsResult | null = infraRouteMetrics && {
    ...infraRouteMetrics,
    trafficSignals: { ...infraRouteMetrics.trafficSignals, count: infraRouteMetrics.trafficSignals.features.length },
    trafficCalming: { ...infraRouteMetrics.trafficCalming, count: infraRouteMetrics.trafficCalming.features.length },
    crossings: { ...infraRouteMetrics.crossings, count: infraRouteMetrics.crossings.features.length },
  };

  return { loadState, pavementStops, infraRouteMetrics: withCounts };
}
